import React, { useEffect, useState, useCallback } from "react";
import * as S from "./AdminFacilities.styled";
import apiClient from "services/api";
import { BuildingFacility } from "services/facilityService";
import AdminFacilityDetail from "./AdminFacilityDetail";
import AdminFacilityDrawer from "./AdminFacilityDrawer";

const AdminFacilities: React.FC = () => {
  const [facilities, setFacilities] = useState<BuildingFacility[]>([]);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchFacilities = useCallback(() => {
    const universityId = localStorage.getItem("adminUniversityId");
    setIsLoading(true);
    apiClient
      .get<BuildingFacility[]>(`/universities/${universityId}/facilities`)
      .then((res) => setFacilities(res.data))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  const filtered = facilities.filter(
    (f) =>
      f.name.toLowerCase().includes(query.toLowerCase()) ||
      (f.building?.name ?? "").toLowerCase().includes(query.toLowerCase()) ||
      (f.location ?? "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <>
      <S.PageHeader>
        <S.PageTitle>시설 목록</S.PageTitle>
        <S.AddButton onClick={() => setIsDrawerOpen(true)}>+ 시설 추가</S.AddButton>
      </S.PageHeader>

      <S.Toolbar>
        <S.SearchWrap>
          <S.SearchIcon>🔍</S.SearchIcon>
          <S.SearchInput
            placeholder="시설명, 건물명, 위치로 검색..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </S.SearchWrap>
        <S.CountText>
          {isLoading ? "로딩 중..." : `총 ${filtered.length}개 시설`}
        </S.CountText>
      </S.Toolbar>

      <S.Card>
        <S.Table>
          <S.Thead>
            <tr>
              <S.Th width="56px">#</S.Th>
              <S.Th width="72px">썸네일</S.Th>
              <S.Th>시설명</S.Th>
              <S.Th width="160px">건물</S.Th>
              <S.Th width="160px">위치</S.Th>
            </tr>
          </S.Thead>
          <S.Tbody>
            {!isLoading && filtered.length === 0 ? (
              <tr>
                <S.EmptyCell colSpan={5}>
                  {query
                    ? `"${query}"에 해당하는 시설이 없습니다.`
                    : "등록된 시설이 없습니다."}
                </S.EmptyCell>
              </tr>
            ) : (
              filtered.map((facility, index) => (
                <S.Tr
                  key={facility.id}
                  onClick={() => setSelectedFacilityId(facility.id)}
                  style={{ cursor: "pointer" }}
                >
                  <S.Td>
                    <S.IndexCell>{index + 1}</S.IndexCell>
                  </S.Td>
                  <S.Td>
                    {facility.thumbnailImage ? (
                      <S.Thumbnail
                        src={facility.thumbnailImage}
                        alt={facility.name}
                      />
                    ) : (
                      <S.ThumbnailPlaceholder>🏛️</S.ThumbnailPlaceholder>
                    )}
                  </S.Td>
                  <S.Td>
                    <S.FacilityName>{facility.name}</S.FacilityName>
                    {facility.purpose && (
                      <S.FacilityPurpose>{facility.purpose}</S.FacilityPurpose>
                    )}
                  </S.Td>
                  <S.Td>
                    <S.MetaText>{facility.building?.name ?? "—"}</S.MetaText>
                  </S.Td>
                  <S.Td>
                    <S.MetaText>{facility.location || "—"}</S.MetaText>
                  </S.Td>
                </S.Tr>
              ))
            )}
          </S.Tbody>
        </S.Table>
      </S.Card>
      {selectedFacilityId !== null && (
        <AdminFacilityDetail
          facilityId={selectedFacilityId}
          onClose={() => setSelectedFacilityId(null)}
          onDeleted={() => {
            setSelectedFacilityId(null);
            fetchFacilities();
          }}
        />
      )}
      {isDrawerOpen && (
        <AdminFacilityDrawer
          onClose={() => setIsDrawerOpen(false)}
          onSuccess={() => {
            setIsDrawerOpen(false);
            fetchFacilities();
          }}
        />
      )}
    </>
  );
};

export default AdminFacilities;
